import { RobotApi, RobotTypeDefinitionItem } from 'api/RobotApi'
import { RobotColorEnum } from 'api/schema'
import clsx from 'clsx'
import { Button } from 'components/Button/Button'
import Dropdown, { IOption } from 'components/Dropdown/Dropdown'
import TextFieldDescribed from 'components/TextFieldDescribed/TextFieldDescribed'
import { useIsMobile } from 'hooks/useIsMobile'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import Modal from 'react-modal'
import { ASBIS_ENABLED, GAUSIUM_ENABLED } from 'utils/constants'
import { IRobotInfo } from 'utils/interfaces'
import { getRobotTypeDisplayName } from 'utils/robotTypeDisplayName'
import type { VendorCode } from 'utils/vendorDisplayName'
import { VENDOR } from 'utils/vendorDisplayName'

import style from './AddRobotsModal.module.css'

interface IAddRobotModal {
  isOpen: boolean
  onClose: () => void
  onSave: () => void
  className?: string
  robotExisting?: IRobotInfo
  robots: IRobotInfo[]
}

const AddRobotModal: React.FC<IAddRobotModal> = props => {
  const { t } = useTranslation()
  const isMobile = useIsMobile()
  const [saving, setSaving] = useState(false)
  const [name, setName] = useState<string>(props.robotExisting?.name || t('robots.new_robot_name'))
  const [description, setDescription] = useState(props.robotExisting?.description || '')
  const [simNumber, setSimNumber] = useState(props.robotExisting?.sim_card_number || '')
  const getID = () => {
    return props.robotExisting ? props.robotExisting.serialNumber : ''
  }
  const [serialId, setSerialId] = useState(getID())

  const robotColorOptions: IOption[] = Object.values(RobotColorEnum)
    .filter(value => typeof value === 'string')
    .map(color => ({
      text: color,
      value: color,
    }))

  const vendorOptions: IOption[] = useMemo(() => {
    const options: IOption[] = [{ text: 'Autonomics', value: VENDOR.AUTONOMICS }]
    if (GAUSIUM_ENABLED) {
      options.push({ text: 'Gausium', value: VENDOR.GAUSIUM })
    }
    if (ASBIS_ENABLED) {
      options.push({ text: 'Asbis', value: VENDOR.ASBIS })
    }
    return options
  }, [])

  const [robotTypeDefinitions, setRobotTypeDefinitions] = useState<RobotTypeDefinitionItem[]>([])
  const [robotTypeOptions, setRobotTypeOptions] = useState<IOption[]>([])
  const [robotType, setRobotType] = useState<string | null>(props.robotExisting?.robot_type ?? null)
  const [robotColor, setRobotColor] = useState(props.robotExisting?.robot_color || 'White')
  const [vendor, setVendor] = useState<VendorCode>((props.robotExisting?.vendor as VendorCode) || VENDOR.AUTONOMICS)

  const [robotTypeOption, setRobotTypeOption] = useState<IOption>({ text: '—', value: '' })

  const [robotColorOption, setRobotColorOption] = useState(
    robotColor ? { text: robotColor, value: robotColor } : robotColorOptions[0]
  )

  const [vendorOption, setVendorOption] = useState(
    () => vendorOptions.find(o => o.value === vendor) ?? vendorOptions[0]
  )

  const loadTypesForVendor = useCallback(
    async (vendorCode: VendorCode, preserveSelection: boolean = false, codeToPreserve: string | null = null) => {
      const list = await RobotApi.fetchRobotTypeDefinitions(vendorCode)
      setRobotTypeDefinitions(list)
      const options: IOption[] = list.map(t => ({
        text: getRobotTypeDisplayName(vendorCode, t.code) || t.display_name,
        value: t.code,
      }))
      setRobotTypeOptions(options)
      if (options.length === 0) return
      if (preserveSelection && codeToPreserve) {
        const found = list.find((t: RobotTypeDefinitionItem) => t.code === codeToPreserve)
        if (found) {
          setRobotType(found.code)
          setRobotTypeOption({
            text: getRobotTypeDisplayName(vendorCode, found.code) || found.display_name,
            value: found.code,
          })
          return
        }
      }
      setRobotType(list[0].code)
      setRobotTypeOption(options[0])
    },
    []
  )

  useEffect(() => {
    if (!props.isOpen) return
    const preserve = !!props.robotExisting
    const codeToPreserve = preserve ? props.robotExisting?.robot_type ?? null : null
    const requestedVendor = vendorOptions.some(o => o.value === vendor) ? vendor : VENDOR.AUTONOMICS
    loadTypesForVendor(requestedVendor, preserve, codeToPreserve)
  }, [props.isOpen, loadTypesForVendor, props.robotExisting, vendor, vendorOptions])

  useEffect(() => {
    if (props.isOpen && props.robotExisting?.robot_type && robotTypeDefinitions.length > 0) {
      const t = robotTypeDefinitions.find((d: RobotTypeDefinitionItem) => d.code === props.robotExisting!.robot_type)
      if (t) {
        setRobotType(t.code)
        setRobotTypeOption({
          text: getRobotTypeDisplayName(vendor, t.code) || t.display_name,
          value: t.code,
        })
      }
    }
  }, [props.isOpen, props.robotExisting?.robot_type, robotTypeDefinitions])

  const onSaveClick = async () => {
    try {
      setSaving(true)
      const selectedType = robotTypeDefinitions.find(t => t.code === robotType)
      const robotTypeCode = selectedType?.code ?? 'Sweeper'
      if (props.robotExisting) {
        await RobotApi.patchRobot(
          props.robotExisting.id,
          name,
          description,
          simNumber,
          robotTypeCode,
          robotColor,
          undefined
        )
      } else {
        await RobotApi.createRobot(
          name,
          description,
          serialId,
          simNumber,
          robotTypeCode,
          robotColor,
          vendorOptions.some(o => o.value === vendor) ? vendor : VENDOR.AUTONOMICS
        )
      }
      props.onSave()
    } catch (error) {
      console.log('Error creating robot')
    }
    setSaving(false)
  }

  const onCancelClick = () => {
    setRobotType(props.robotExisting?.robot_type ?? null)
    if (robotTypeOptions.length > 0) {
      const opt = robotTypeOptions.find(o => o.value === props.robotExisting?.robot_type)
      setRobotTypeOption(opt ?? robotTypeOptions[0])
    }

    setRobotColor(props.robotExisting?.robot_color || 'White')
    setRobotColorOption(
      props.robotExisting
        ? { text: props.robotExisting.robot_color, value: props.robotExisting.robot_color }
        : robotColorOptions[0]
    )

    const restoredVendor = ((props.robotExisting?.vendor as VendorCode) || VENDOR.AUTONOMICS) as VendorCode
    setVendor(restoredVendor)
    setVendorOption(vendorOptions.find(o => o.value === restoredVendor) ?? vendorOptions[0])

    setName(props.robotExisting?.name || t('robots.new_robot_name'))
    setDescription(props.robotExisting?.description || '')
    setSerialId(getID())

    props.onClose()
  }

  const onTypeSelected = (e: IOption) => {
    setRobotTypeOption(e)
    setRobotType(typeof e.value === 'string' ? e.value : null)
  }

  const onColorSelected = (e: IOption) => {
    setRobotColorOption(e)
    setRobotColor(e.value as string)
  }

  const onVendorSelected = (e: IOption) => {
    setVendorOption(e)
    const newVendor = e.value as VendorCode
    setVendor(newVendor)
    loadTypesForVendor(newVendor, false, null)
  }

  const serialIdError = useMemo(() => {
    return !props.robotExisting && !!props.robots.find(robot => robot.serialNumber === serialId)
  }, [props.robotExisting, props.robots, serialId])

  const isNameValid = /^[a-zA-Z0-9\s\-_]+$/.test(name) && /[a-zA-Z0-9]/.test(name)
  const isSaveDisabled = name === '' || !isNameValid || !serialId || serialIdError

  return (
    <Modal
      isOpen={props.isOpen}
      onRequestClose={props.onClose}
      className={clsx(style.root, props.className)}
      shouldCloseOnOverlayClick={true}
      style={{ overlay: { zIndex: 100 } }}
      ariaHideApp={false}
    >
      <div className={style.titleBlock}>
        <div className={style.title}>{props.robotExisting ? t('robots.edit_existing') : t('robots.add_new')}</div>
      </div>
      <div className={style.row}>
        <TextFieldDescribed
          className={style.textfield}
          description={t('robots.robot_name')}
          defaultValue={name}
          placeholder={t('robots.robot_name_placeholder')}
          onChange={e => setName(e.target.value)}
          error={'Name must contain letters or numbers'}
          hasError={!isNameValid}
          small={isMobile}
        />
        <TextFieldDescribed
          className={style.textfield}
          description={t('robots.robot_serial_id')}
          defaultValue={String(serialId)}
          placeholder={t('robots.robot_serial_id_placeholder')}
          onChange={e => setSerialId(e.target.value)}
          disabled={!!props.robotExisting}
          type="text"
          error="Serial ID must be unique"
          hasError={serialIdError}
          small={isMobile}
        />
      </div>

      <div className={style.row}>
        <Dropdown
          className={style.textfield}
          currentOption={vendorOption}
          options={vendorOptions}
          onClickOption={onVendorSelected}
          description="Select robot's vendor:"
          disabled={!!props.robotExisting}
          useSmallDropdownStyle={isMobile}
        />
        <Dropdown
          className={style.textfield}
          currentOption={robotTypeOption}
          options={robotTypeOptions}
          onClickOption={onTypeSelected}
          description="Select robot's type:"
          disabled={!!props.robotExisting}
          useSmallDropdownStyle={isMobile}
        />
        <Dropdown
          className={style.textfield}
          currentOption={robotColorOption}
          options={robotColorOptions}
          onClickOption={onColorSelected}
          description="Select robot's color:"
          useSmallDropdownStyle={isMobile}
        />
      </div>

      <div className={style.row}>
        <TextFieldDescribed
          className={style.textfield}
          description={t('robots.sim_number')}
          defaultValue={simNumber}
          placeholder={t('robots.sim_number_placeholder')}
          onChange={e => setSimNumber(e.target.value)}
          small={isMobile}
        />
      </div>
      <div className={style.footer}>
        <Button.Outlined className={style.btn} disabled={saving} onClick={onCancelClick}>
          {t('common.cancel')}
        </Button.Outlined>
        <Button.Standart
          className={style.btn}
          disabled={saving || isSaveDisabled}
          loading={saving}
          onClick={onSaveClick}
        >
          {t('common.save')}
        </Button.Standart>
      </div>
    </Modal>
  )
}

export default AddRobotModal
